import { CommodityDetail } from "features/contracts/enums/commodityDetail";
import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import EntityLinkDTO from "../dtos/EntityLinkDTO";
import { InvoiceType } from "../enums/invoiceType";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";

import styles from "./invoiceinfo.module.scss";
import { Entity } from "features/shared/enums/entity";
import ContractLink from "./ContractLink";
import DateFormatter from "features/shared/components/formatters/DateFormatter";
import { InvoiceSubType } from "../enums/invoiceSubType";
import { isProductionInvoice } from "../helpers/invoiceHelper";
import { containsFuelingCommodity } from "features/contracts/helpers/commodityDetailHelper";

interface IProps {
  invoiceType: InvoiceType;
  invoiceSubType: InvoiceSubType;
  commodityDetails: CommodityDetail[];
  entityLinks: EntityLinkDTO[];
  timePeriodFrom?: Date;
  timePeriodTo?: Date;
  mobileBehaviour?: boolean;
  showTimePeriod?: boolean;
}

const InvoiceInfo: React.FC<IProps> = (props) => {
  // tanslations
  const { t } = useTranslation(["invoice\\enums\\invoiceType"]);
  const tLabels = useTranslation(["invoice\\invoice"]).t;

  // props
  const { invoiceType, invoiceSubType, commodityDetails, entityLinks, timePeriodFrom, timePeriodTo, mobileBehaviour, showTimePeriod } = props;

  // variables
  const isFuelingInvoice = containsFuelingCommodity(commodityDetails);

  // helpers
  const getLinkSectionTitle = (): string => { 
    if (isFuelingInvoice) {
      return tLabels("invoiceInfo.contractIdentification");
    } else {
      return tLabels("invoiceInfo.supplyPointIdentification");
    }
  }

  const getMoreLinksTitle = (): string => { 
    if (isFuelingInvoice) {
      return tLabels("invoiceInfo.moreContracts");
    } else {
      return tLabels("invoiceInfo.moreSupplyPoint");
    }
  }

  // sub renders
  const renderLinkSection = (entityLinks: EntityLinkDTO[], mobileBehaviour?: boolean): ReactElement | undefined => {
    if (entityLinks) {
      let contractLinks = entityLinks.filter(x => x.entity === Entity.Contract);
      let labelClass = mobileBehaviour ? styles.billingLabelTextMobile : styles.billingLabelText;

      if (contractLinks.length > 0) {
        return (
          <div className="row no-gutters">
            <div className="col-auto">
              <div className={labelClass}>{getLinkSectionTitle()}</div>
            </div>
            <div className="col">
              {renderOpmLink(contractLinks, mobileBehaviour)}
            </div>
          </div>
        );
      }
    }
  }

  const renderOpmLink = (entityLinks: EntityLinkDTO[], mobileBehaviour?: boolean): ReactElement => {    

    if (entityLinks.length === 1) {
      let entityLink = entityLinks[0];

      return (
        <ContractLink
          entityLink={entityLink}
          className={mobileBehaviour ? styles.billingLinkMobile : ""}
          isFuelingContract={isFuelingInvoice}
        />
      );
    } else {
      return (
      <span className={`text-truncate text-secondary small mw-100 d-inline-block`}>
        {getMoreLinksTitle()}
      </span>
      );
    }
  }

  const renderCommodityIcons = (commodityDetails: CommodityDetail[]): ReactElement[] => {
    let result: ReactElement[] = []; 

    for (let i = 0; i < commodityDetails?.length; i++) {
      const detail = commodityDetails[i];
      
      if (i > 0) {
        let divider = <span className={styles.billingDivider} key={i + 1}>/</span>  
        result.push(divider);
      }

      let elem = <CommodityIcon commodityDetail={detail} includeColor key={i} isProduction={isProductionInvoice(invoiceSubType)} />;
      result.push(elem);
    }

    return result;
  }

  const renderTimePeriod = () => {
    if (showTimePeriod && timePeriodFrom && timePeriodTo) {
      return (
        <small className="d-none d-xl-inline text-muted ml-1">
          ({tLabels("invoiceInfo.timePeriod")} <DateFormatter date={timePeriodFrom} /> - <DateFormatter date={timePeriodTo} />)
        </small>
      );
    }    
  }

  // render
  if (mobileBehaviour) {
    return (
      <div className="row no-gutters d-sm-block">
        <div className="col-auto pr-2 pr-sm-0">
          <div className="row no-gutters">
            <div className="col-auto">
              <div className={styles.billingLabelMobile}>
                {renderCommodityIcons(commodityDetails)}
              </div>
            </div>
            <div className="col">
              <div className="text-truncate">
                {t(invoiceType)}
                {renderTimePeriod()}
              </div>
            </div>
          </div>
        </div>
        <div className="col">
          {renderLinkSection(entityLinks, mobileBehaviour)}
        </div>
      </div>
    );
  } else {
    return (
      <>
        <div className="row no-gutters">
          <div className="col-auto">
            <div className={styles.billingLabel}>
              {renderCommodityIcons(commodityDetails)}
            </div>
          </div>
          <div className="col">
            <div className="text-truncate">
              {t(invoiceType)}
              {renderTimePeriod()}
            </div>
          </div>
        </div>
        {renderLinkSection(entityLinks)}
      </>
    );
  }
};

export default InvoiceInfo;